import * as React from 'react';
import {
  IAthlete,
  IExternalProperties,
  IGroup,
  ITeam,
} from '../../../../data/types';
import { useListSearch } from '../../../../hooks/useListSearch';
import { keyValueEntries, KVEntry } from '../../../../utils/key_value';
import { FullHeightColumn } from '../../../layout/Column';
import { Row } from '../../../layout/Row';
import { ScrollableArea } from '../../../layout/ScrollableArea';
import { ClearAllButton, SelectAllButton } from '../../../shared/Button';
import { Count } from '../../../shared/Count';
import { ControlSpacer } from '../../../shared/form/ControlSpacer';
import { HeightInput } from '../../../shared/form/HeightInput';
import { KeyValueControl } from '../../../shared/form/KeyValueControl';
import { ListControlLabel } from '../../../shared/form/ListControlLabel';
import { TextField } from '../../../shared/form/TextField';
import { InlineUploader } from '../../../shared/InlineUploader';
import { ListControlContainer } from '../../../shared/List/ListControlContainer';
import { MultiGroupSelectList } from '../groups/MultiGroupSelectList';
import { MultiTeamSelectList } from '../teams/MultiTeamSelectList';
import { IErrorLookup } from './validation';

interface ITeamManagementProps {
  teams: ITeam[];
  selectedTeams: ITeam[];
  teamSelectionHandler: (team: ITeam, checked: boolean) => void;
  clearAllTeams: () => void;
  selectAllTeams: () => void;
  enforceUniqueAthleteId?: boolean;
}

interface IGroupManagementProps {
  groups: IGroup[];
  selectedGroups: IGroup[];
  groupSelectionHandler: (group: IGroup, checked: boolean) => void;
  clearAllGroups: () => void;
  selectAllGroups: () => void;
}

interface IProps {
  athlete: Partial<IAthlete>;
  athletes: IAthlete[];
  onImageChange: (a: Partial<IAthlete> | Partial<IAthlete>) => void;
  errorLookup: IErrorLookup;
  onChange: (
    name: string,
    value: string | number | { [key: string]: string }
  ) => void;
}

const AthleteForm = (
  props: IProps & ITeamManagementProps & IGroupManagementProps
): React.ReactElement => {
  const {
    list: groups,
    search: searchGroups,
    clear: clearGroupSearch,
    searchTerm: groupSearchTerm,
  } = useListSearch(props.groups);

  const {
    list: teams,
    search: searchTeams,
    clear: clearTeamsSearch,
    searchTerm: teamSearchTerm,
  } = useListSearch(props.teams);
  /**
   * @description handles image change
   * @param img
   */
  const onImageChange = (img: { url: string }): void => {
    props.onImageChange({ ...athlete, image: img && img.url ? img.url : null });
  };
  /**
   * @description handles name change
   * @param value
   */
  const onNameChange = (value: string): void => props.onChange('name', value);

  /**
   * @description handles position change
   * @param value
   */
  const onPositionChange = (value: string): void =>
    props.onChange('position', value);

  /**
   * @description handles sport change
   * @param value
   */
  const onSportChange = (value: string): void => props.onChange('sport', value);

  /**
   * @description handles dob change
   * @param value
   */
  const onDobChange = (value: string): void => props.onChange('dob', value);

  const onUniqueIdChange = (value: string): void =>
    props.onChange('external:uniqueId', value);

  const onExternalChange = (entries: KVEntry[]): void => {
    const externalProps: IExternalProperties = {};
    for (const entry of entries) {
      externalProps[`${entry[1]}`] = entry[2];
    }
    if (props.athlete.external?.uniqueId) {
      externalProps.uniqueId = props.athlete.external?.uniqueId;
    }
    props.onChange('external', externalProps);
  };

  /**
   * @description handles height change
   * @param value
   */
  const onHeightChange = (value: number): void =>
    props.onChange('height', value);

  const { errorLookup: errors, athlete } = props;

  const nameError = React.useMemo((): string | null => {
    if (errors?.name?.hasError) return errors.name.message;

    return null;
  }, [JSON.stringify(errors)]);

  const uniqueIdError = React.useMemo((): string | null => {
    if (errors?.uniqueId?.hasError) return errors.uniqueId.message;

    return null;
  }, [JSON.stringify(errors)]);

  return (
    <Row shouldCollapse>
      <FullHeightColumn
        style={{ padding: '20px', justifyContent: 'flex-start' }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <InlineUploader
            path={athlete.image ? athlete.image : ''}
            onChange={onImageChange}
            onDelete={onImageChange}
          />
        </div>
        <ControlSpacer>
          <TextField
            name='name'
            onChange={onNameChange}
            value={athlete.name || ''}
            label='Name (required)'
            placeholder='Name...'
            type='text'
            error={nameError}
          />
        </ControlSpacer>
        <ControlSpacer>
          <TextField
            name='uniquId'
            onChange={onUniqueIdChange}
            value={athlete.external?.uniqueId || ''}
            label={`${
              props.enforceUniqueAthleteId
                ? 'Unique Id (required)'
                : 'Unique Id'
            }`}
            placeholder='Unique Id...'
            type='text'
            error={uniqueIdError}
          />
        </ControlSpacer>
        <ControlSpacer>
          <TextField
            name='position'
            onChange={onPositionChange}
            value={athlete.position || ''}
            label='Position'
            placeholder='Position...'
            type='text'
          />
        </ControlSpacer>
        <ControlSpacer>
          <TextField
            name='sport'
            onChange={onSportChange}
            value={athlete.sport || ''}
            label='Sport'
            placeholder='Sport...'
            type='text'
          />
        </ControlSpacer>
        <ControlSpacer>
          <TextField
            name='dob'
            onChange={onDobChange}
            value={athlete.dob || ''}
            label='Birth Year'
            placeholder='YYYY'
            type='text'
          />
        </ControlSpacer>
        <ControlSpacer>
          <HeightInput value={athlete.height} onChange={onHeightChange} />
        </ControlSpacer>
        <ControlSpacer>
          <KeyValueControl
            kvEntries={keyValueEntries(athlete?.external ?? {}, ['uniqueId'])}
            onChange={onExternalChange}
          />
        </ControlSpacer>
      </FullHeightColumn>
      <FullHeightColumn>
        <ListControlLabel
          label='Select Team(s)'
          helpText=' An athlete must be assigned to at least one team'
          onSearchClearHandler={clearTeamsSearch}
          searchValue={teamSearchTerm}
          onSearchHandler={searchTeams}
        >
          <ClearAllButton
            onClick={props.clearAllTeams}
            disabled={props.selectedTeams.length === 0}
          />
          <SelectAllButton
            onClick={props.selectAllTeams}
            disabled={props.selectedTeams.length === props.teams.length}
          />
          <Count value={props.selectedTeams.length} />
        </ListControlLabel>
        <ListControlContainer>
          <ScrollableArea
            style={{
              flex: 1,
            }}
          >
            <MultiTeamSelectList
              teams={teams}
              selectedTeams={props.selectedTeams}
              onSelect={props.teamSelectionHandler}
              emptyMessage={
                teamSearchTerm.length > 0
                  ? `No teams matching '${teamSearchTerm}'`
                  : null
              }
            />
          </ScrollableArea>
        </ListControlContainer>
      </FullHeightColumn>
      <FullHeightColumn>
        <ListControlLabel
          label='Select Group(s)'
          onSearchClearHandler={clearGroupSearch}
          searchValue={groupSearchTerm}
          onSearchHandler={searchGroups}
        >
          <ClearAllButton
            onClick={props.clearAllGroups}
            disabled={props.selectedGroups.length === 0}
          />
          <SelectAllButton
            onClick={props.selectAllGroups}
            disabled={props.selectedGroups.length === props.groups.length}
          />
          <Count value={props.selectedGroups.length} />
        </ListControlLabel>
        <ListControlContainer>
          <ScrollableArea
            style={{
              flex: 1,
            }}
          >
            <MultiGroupSelectList
              teams={props.teams}
              selectedTeams={props.selectedTeams}
              disabled={props.selectedTeams.length < 1}
              groups={groups}
              selectedGroups={props.selectedGroups}
              onSelect={props.groupSelectionHandler}
              emptyMessage={
                groupSearchTerm.length > 0
                  ? `No groups matching '${groupSearchTerm}'`
                  : null
              }
            />
          </ScrollableArea>
        </ListControlContainer>
      </FullHeightColumn>
    </Row>
  );
};

export { AthleteForm };
